import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import {
  useLoginRider,
  useLoginDriver,
} from "../../hooks/auth-operations/useAuthMutations";
import { useAuthStore, useUserStore } from "../../stores";

function Login({ isDriver, onForgotPassword }) {
  // Global state.
  const { setAccessToken, setIsAuth } = useAuthStore();
  const { setUserId, setUserType, setUserName } = useUserStore();

  // State for login form.
  const [showPassword, setShowPassword] = useState(false);
  const [isValidLogin, setIsValidLogin] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  // Form values.
  const emailRef = useRef();
  const passwordRef = useRef();

  // Set up route navigation.
  const navigate = useNavigate();

  // Get login mutations.
  const { loginRider } = useLoginRider();
  const { loginDriver } = useLoginDriver();

  // Login Submit.
  async function handleLoginSubmit(e) {
    e.preventDefault();

    // Reset error message.
    setErrorMessage("");

    // Trim the email — autofill and mobile keyboards often append a stray
    // space, and the server rejects "user@example.com " as a bad login.
    const loginObj = {
      email: emailRef.current.value.trim(),
      password: passwordRef.current.value,
    };

    // Choose the mutation based on the isDriver prop.
    const loginMutation = isDriver ? loginDriver : loginRider;

    try {
      const response = await loginMutation({
        variables: loginObj,
      });

      const userData = response.data[isDriver ? "loginDriver" : "loginRider"];

      // Set global state.
      setAccessToken(userData.accessToken);
      setIsAuth(true);
      setUserId(userData[isDriver ? "driver" : "rider"].id);
      setUserType(isDriver ? "driver" : "rider");
      setUserName(userData[isDriver ? "driver" : "rider"].name);

      // Set local state.
      setIsValidLogin(true);
      setShowPassword(false);

      // Navigate to the home page based on userType.
      navigate(`/${isDriver ? "driver" : "rider"}home`);
    } catch (error) {
      console.error("Login error:", error);

      setIsValidLogin(false);
      setErrorMessage("An error occurred during login.");
    }
  }

  return (
    <>
      <form
        onSubmit={handleLoginSubmit}
        className="flex flex-col w-full center-all"
      >
        <h3 className="text-highlight-blue pt-6 pb-4 text-xl">
          {isDriver ? "Driver Login" : "Login"}
        </h3>

        <input
          type="email"
          id="login-email"
          name="email"
          autoComplete="email"
          ref={emailRef}
          placeholder="Email"
          className={`modal-input-layout modal-input-border ${
            isValidLogin ? "" : "modal-input-error"
          }`}
        />

        <div className="relative w-4/5 m-4">
          <input
            type={showPassword ? "text" : "password"}
            id="login-password"
            name="password"
            autoComplete="current-password"
            ref={passwordRef}
            placeholder="Password"
            className={`text-black p-2 rounded w-full modal-input-border ${
              isValidLogin ? "" : "modal-input-error"
            }`}
          />
          <FaEye
            className="absolute cursor-pointer top-2.5 right-2.5 w-6 h-6 text-black"
            onClick={() => setShowPassword((prevState) => !prevState)}
          />
        </div>

        <button type="submit" className="btn btn-green btn-md">
          Login
        </button>
      </form>

      <button
        type="button"
        onClick={() => onForgotPassword && onForgotPassword()}
        className="mt-3 text-sm text-highlight-blue hover:text-white transition-colors underline"
      >
        Forgot Password?
      </button>

      <p
        className={`mt-2 text-sm text-red-600 dark:text-red-500 ${
          isValidLogin ? "hidden" : "block"
        }`}
      >
        {errorMessage}
      </p>
    </>
  );
}

export default Login;
